import { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { toast } from 'react-toastify';

import { addMinutes } from 'date-fns';
import { saveAs } from 'file-saver';
import PropTypes from 'prop-types';

import { useTransl } from 'shared/lib/i18n';

import VideoDownloadComponent from './VideoDownloadComponent';

import { notifyUser } from '../Main/actions';
import { onModalClose, showConfirmModal } from '../Modal/actions';
import { getCameraOnTimeIntervals } from './actions';

import { formatDate } from '../../utils';
import {
  formatEESeconds,
  formatEETimestamp,
  getEEBaseUrl,
  getEEDownloadDurationOptions,
  getEEVideoListQuery,
  inCurrentTimezone,
  inUTCTimezone,
  parseEESeconds,
} from './utils';

import { dateOnlyFormat } from '../../staticData';

const VideoDownloadContainer = (props) => {
  const { t } = useTransl();
  const { downloadData, notifyUser } = props;
  const {
    config,
    currentVideoTime,
    setDownloadData,
    buildingName,
    cameraName,
  } = downloadData;

  const [startDate, setStartDate] = useState(currentVideoTime);
  const [duration, setDuration] = useState(getEEDownloadDurationOptions()[5]);
  const [videoList, setVideoList] = useState([]);
  const [hasNewParams, setHasNewParams] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [activeDownloads, setActiveDownloads] = useState([]);

  const videoDownloadRef = useRef(null);

  const getVideoList = async () => {
    try {
      setIsLoading(true);
      const queryParams = {
        ...config,
        start: formatEETimestamp(parseEESeconds(startDate)),
        end: formatEETimestamp(
          addMinutes(parseEESeconds(startDate), duration?.value),
        ),
      };

      const res = await getCameraOnTimeIntervals(
        getEEVideoListQuery(queryParams),
      );
      if (res?.data) setVideoList(res.data);
      setIsLoading(false);
    } catch (err) {
      toast.warn(t('camera.cannotLoadData'));
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getVideoList();
    return () =>
      videoDownloadRef.current && clearTimeout(videoDownloadRef.current);
  }, []);

  const onSetStartDate = (date) => {
    const utcDate = inUTCTimezone(formatEESeconds(date));
    setStartDate(utcDate);
    setHasNewParams(true);
  };

  const onDurationChange = (event, duration) => {
    setDuration(duration);
    setHasNewParams(true);
  };

  const addActiveDownload = (clipId) => {
    if (activeDownloads.includes(clipId)) return;
    const newIds = [...activeDownloads, clipId];
    setActiveDownloads(newIds);
  };
  const removeActiveDownload = (clipId) => {
    const newIds = [...activeDownloads].filter((id) => id !== clipId);
    setActiveDownloads(newIds);
  };

  const getDownloadUrl = ({ s, e }) => {
    const { esn, auth_key } = config;
    const baseUrl = getEEBaseUrl();
    const fileUrl = `${baseUrl}asset/play/video.mp4`;
    const params = `?id=${esn}&start_timestamp=${s}&end_timestamp=${e}&index=true&A=${auth_key}`;
    return fileUrl + params;
  };

  const downloadVideoClip = (title, videoTime, clipId, tryNumber = 1) => {
    if (tryNumber > 10) {
      notifyUser(t('camera.downloadError'));
      removeActiveDownload(clipId);
      return null;
    }

    const downloadUrl = getDownloadUrl(videoTime);
    const vidHeaders = new Headers();
    vidHeaders.append('pragma', 'no-cache');
    vidHeaders.append('cache-control', 'no-cache');

    const initConfig = {
      method: 'GET',
      headers: vidHeaders,
    };

    const checkVideoStatus = (res) => {
      const { status } = res;
      switch (status) {
        case 200:
          return res.blob();
        default:
          videoDownloadRef.current = setTimeout(
            () => downloadVideoClip(title, videoTime, clipId, tryNumber + 1),
            1.5 * 1000,
          );
          return null;
      }
    };

    tryNumber === 1 && addActiveDownload(clipId);
    return fetch(downloadUrl, initConfig)
      .then((res) => checkVideoStatus(res))
      .then((blob) => {
        if (!blob) return;
        saveAs(blob, title);
        removeActiveDownload(clipId);
      })
      .catch(() => {
        videoDownloadRef.current = setTimeout(
          () => downloadVideoClip(title, videoTime, clipId, tryNumber + 1),
          1.5 * 1000,
        );
      });
  };

  const getVideoDownloadTitle = (time, duration) => {
    const date = formatDate(
      parseEESeconds(inCurrentTimezone(startDate)),
      true,
      dateOnlyFormat,
    );
    return `${buildingName} — ${cameraName} — ${date} — ${time} — ${duration}.mp4`;
  };

  return (
    <VideoDownloadComponent
      setDownloadData={setDownloadData}
      startDate={parseEESeconds(inCurrentTimezone(startDate))}
      onSetStartDate={onSetStartDate}
      duration={duration}
      onDurationChange={onDurationChange}
      isLoading={isLoading}
      videoList={videoList}
      activeDownloads={activeDownloads}
      getVideoDownloadTitle={getVideoDownloadTitle}
      downloadVideoClip={downloadVideoClip}
      hasNewParams={hasNewParams}
      getVideoList={getVideoList}
      setHasNewParams={setHasNewParams}
    />
  );
};

VideoDownloadContainer.defaultProps = {};

VideoDownloadContainer.propTypes = {
  downloadData: PropTypes.shape({
    config: PropTypes.instanceOf(Object),
    currentVideoTime: PropTypes.number,
    setDownloadData: PropTypes.func,
    buildingName: PropTypes.string.isRequired,
    cameraName: PropTypes.string.isRequired,
  }).isRequired,
  notifyUser: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  showModal: (modalProps) => showConfirmModal(dispatch, modalProps),
  closeModal: () => onModalClose(dispatch),
  notifyUser: (msg) => notifyUser(dispatch, msg, false),
});

export default connect(
  null,
  mapDispatchToProps,
)(withRouter(VideoDownloadContainer));
